import { useState } from "react";
import { NotOfferedNotice } from "../app/NotOfferedNotice";
import { Link } from "react-router-dom";
import { ChevronDown, Loader2 } from "lucide-react";

export const SHORTLIST_STATUS = {
  scored: { label: "Scored", cls: "bg-emerald-50 border-emerald-300 text-emerald-800" },
  needs_info: { label: "Missing information", cls: "bg-amber-50 border-amber-300 text-amber-900" },
  excluded: { label: "Ruled out by a requirement", cls: "bg-rose-50 border-rose-300 text-rose-800" },
  outside_countries: { label: "Outside your selected countries", cls: "bg-sky-50 border-sky-300 text-sky-900" },
  unassessed: { label: "Not yet assessed", cls: "bg-stone-100 border-stone-300 text-stone-700" },
};
const ORDER = ["scored", "needs_info", "excluded", "outside_countries", "unassessed"];

const keyOf = (it) => it.degree_id || `uni-${it.university_id}`;

function ScoreLine({ row, testId }) {
  return (
    <p data-testid={testId} className="text-[13px] text-stone-800">
      Overall match <span className="font-mono font-semibold text-[#4C1D95]">{row.overall_match}%</span>
      {row.academic?.label && <> · Academic: {row.academic.label}</>}
    </p>
  );
}

function Gates({ it }) {
  const extra = [...(it.financial_gates || []), ...(it.visa_gates || [])].filter((g) => g !== it.reason);
  if (!extra.length) return null;
  return (
    <ul className="mt-1 list-disc pl-5 text-[12px] text-stone-600">
      {extra.map((g) => <li key={g}>{g}</li>)}
    </ul>
  );
}

function ShortlistItem({ it, onEditProfile, onRemove, renderCard, hiddenReason }) {
  const [open, setOpen] = useState(false);
  const [checkAnyway, setCheckAnyway] = useState(false);
  const k = keyOf(it);
  const st = SHORTLIST_STATUS[it.status] || SHORTLIST_STATUS.unassessed;
  const row = it.row;
  const hidden = row ? hiddenReason(row) : null;
  const alt = it.if_included;
  const btn = "rounded-full border px-3 py-1 text-[11px] transition-colors";
  return (
    <li data-testid={`shortlist-item-${k}`} data-status={it.status} className="rounded-lg border border-stone-200 bg-white p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-stone-900 break-words">
            {it.degree_id && it.degree_name
              ? <Link to={`/degree/${it.degree_id}`} className="hover:underline">{it.degree_name}</Link>
              : (it.degree_name || "Whole university")}
          </div>
          <div className="text-[12px] text-stone-500">{it.university_name || it.university_id}{it.country ? ` · ${it.country}` : ""}</div>
        </div>
        <span data-testid={`shortlist-status-${k}`} className={`shrink-0 rounded border px-2 py-0.5 text-[11px] ${st.cls}`}>{st.label}</span>
      </div>
      <div className="mt-2 space-y-1">
        {row && <ScoreLine row={row} testId={`shortlist-score-${k}`} />}
        {it.not_offered && <NotOfferedNotice info={it.not_offered} testId={`shortlist-not-offered-${k}`} />}
        {it.reason && <p data-testid={`shortlist-reason-${k}`} className="text-[12px] text-stone-700 leading-snug">{it.reason}</p>}
        {it.status === "excluded" && <Gates it={it} />}
        {hidden && <p data-testid={`shortlist-hidden-${k}`} className="text-[12px] text-amber-800">Not in the list below: {hidden}</p>}
        {it.status === "outside_countries" && checkAnyway && alt && (
          <div data-testid={`shortlist-if-included-${k}`} className="rounded border border-dashed border-sky-300 bg-sky-50/50 p-2 text-[12px]">
            <p className="font-semibold text-sky-900">If you added {it.country} to your countries:</p>
            {alt.row ? <ScoreLine row={alt.row} testId={`shortlist-if-included-score-${k}`} /> : <p>{alt.reason}</p>}
            <p className="text-stone-500 mt-1">Your country choice hasn't changed. Edit your answers to include {it.country} in your search.</p>
          </div>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {row && renderCard && (
          <button type="button" data-testid={`shortlist-toggle-card-${k}`} onClick={() => setOpen((v) => !v)} aria-expanded={open}
            className={`${btn} border-stone-300 text-stone-700 hover:border-[#4C1D95] hover:text-[#4C1D95]`}>
            {open ? "Hide full scores" : "Show full scores"}
          </button>
        )}
        {it.status === "outside_countries" && (
          <button type="button" data-testid={`shortlist-check-anyway-${k}`} onClick={() => setCheckAnyway((v) => !v)} aria-expanded={checkAnyway}
            className={`${btn} border-sky-300 text-sky-900 hover:bg-sky-50`}>
            {checkAnyway ? "Hide check" : "Check it anyway"}
          </button>
        )}
        {(it.status === "outside_countries" || it.status === "excluded" || it.status === "needs_info" || (it.status === "unassessed" && it.degree_id)) && onEditProfile && (
          <button type="button" data-testid={`shortlist-edit-${k}`} onClick={onEditProfile}
            className={`${btn} border-stone-300 text-stone-700 hover:border-[#4C1D95] hover:text-[#4C1D95]`}>
            {it.status === "outside_countries" ? "Edit my countries" : "Edit my answers"}
          </button>
        )}
        <button type="button" data-testid={`shortlist-remove-${k}`} onClick={() => onRemove(it)}
          className={`${btn} border-stone-200 text-stone-500 hover:border-rose-300 hover:text-rose-700`}>
          Remove from shortlist
        </button>
      </div>
      {open && row && <div className="mt-3">{renderCard(row)}</div>}
    </li>
  );
}

export function ShortlistSection({ assess, loading, error, onRetry, ...itemProps }) {
  const [open, setOpen] = useState(true);
  const items = [...(assess?.items || [])].sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status));
  const n = items.length;
  if (!loading && !error && n === 0) return null;
  return (
    <section data-testid="adv-shortlist-section" className="rounded-xl border-2 border-[#4C1D95]/30 bg-violet-50/40 p-4 print:hidden">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" data-testid="adv-shortlist-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}
          className="inline-flex items-center gap-2 font-editorial text-xl text-stone-900">
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "" : "-rotate-90"}`} />
          Your shortlist <span data-testid="adv-shortlist-count">({n})</span>
          {loading && <Loader2 data-testid="adv-shortlist-loading" className="h-4 w-4 animate-spin text-[#4C1D95]" />}
        </button>
      </div>
      {open && (
        <>
          <p className="mt-1 text-[12px] text-stone-600">
            Every course you've saved, checked against this Advanced profile. Saving a course never changes its score, its chance of a place or the balanced portfolio.
          </p>
          {n > 0 && (
            <p data-testid="adv-shortlist-summary" className="mt-1 text-[12px] text-stone-600">
              {ORDER.filter((s) => assess.counts?.[s]).map((s) => `${assess.counts[s]} ${SHORTLIST_STATUS[s].label.toLowerCase()}`).join(" · ")}
            </p>
          )}
          {error && (
            <p role="alert" data-testid="adv-shortlist-error" className="mt-2 text-[12px] text-rose-700">
              Couldn't check your shortlist.{" "}
              <button type="button" onClick={onRetry} className="underline" data-testid="adv-shortlist-retry">Try again</button>
            </p>
          )}
          <ul className="mt-3 space-y-2">
            {items.map((it) => <ShortlistItem key={keyOf(it)} it={it} {...itemProps} />)}
          </ul>
        </>
      )}
    </section>
  );
}
